import encodeQuery from "@splitfire-agency/raiden-library/dist/libraries/utils/encodeQuery";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRInfinite from "swr/infinite";
import useApiFetcher from "../../hooks/useApiFetcher";

/**
 * @template {{}} T
 * @typedef {Object} Props
 * @property {T} defaultValues
 * @property {T} initialFields
 * @property {(fields: T, key: number) => string | null} generateUrl
 * @property {boolean} useRouterUrl
 * @property {boolean} autoSubmit
 */

/**
 * Un hook permettant d'initialiser la valeur du contexte SearchInfiniteContext
 * @template {{}} T
 * @param {Props<T>} props
 * @returns {import("./SearchInfiniteProvider").SearchInfiniteContextValue<T>}
 */
export function useSearchInfinite({
  defaultValues,
  initialFields,
  generateUrl,
  useRouterUrl,
  autoSubmit,
}) {
  const apiFetcher = useApiFetcher();

  const [submittedFields, setSubmittedFields] = useState(
    /** @type {T} */ (initialFields),
  );

  const { pathname, replace } = useRouter();

  /** @type {import("react-hook-form").UseFormReturn<T>} */
  const form = useForm({
    values: useRouterUrl ? initialFields : undefined, // updates the form values when the router values change
    // @ts-ignore
    defaultValues: useRouterUrl ? initialFields : defaultValues,
  });

  const getKey = useCallback(
    (key) => {
      return generateUrl(submittedFields, key);
    },
    [generateUrl, submittedFields],
  );

  const swrInfiniteResponse = useSWRInfinite(getKey, apiFetcher, {
    dedupingInterval: 0,
    revalidateFirstPage: false,
  });
  const { mutate } = swrInfiniteResponse;

  const search = useCallback(
    (values) => {
      if (useRouterUrl) {
        replace(
          `${pathname}${encodeQuery(values, { appendPrefix: true })}`,
          undefined,
          {
            shallow: true,
          },
        );
      }
      // refresh if the values are the same
      if (JSON.stringify(values) === JSON.stringify(submittedFields)) {
        mutate();
      }
      setSubmittedFields(values);
      form.reset(values);
    },
    [useRouterUrl, submittedFields, form, replace, pathname, mutate],
  );

  const submit = useMemo(() => {
    return form.handleSubmit((values) => {
      if (form.formState.isDirty) {
        search(values);
      }
    });
  }, [form, search]);

  const appliedFilters = useMemo(() => {
    // for each key in submitted fields
    return Object.keys(submittedFields).reduce((acc, key) => {
      // if the submitted value is different from the default value
      if (Array.isArray(submittedFields[key])) {
        const isDifferent = submittedFields[key].some(
          (value) => !defaultValues[key].includes(value),
        );
        if (isDifferent) {
          acc = [...acc, key];
        }
      } else if (submittedFields[key] !== defaultValues[key]) {
        acc = [...acc, key];
      }
      return acc;
    }, /** @type {string[]} */ ([]));
  }, [defaultValues, submittedFields]);

  const reset = useCallback(() => {
    Object.keys(defaultValues).forEach((key) => {
      // @ts-ignore
      form.setValue(key, defaultValues[key], { shouldDirty: true });
    });
    if (autoSubmit === false) {
      search(defaultValues);
    }
  }, [defaultValues, autoSubmit, form, search]);

  const searchInfiniteContextValue = useMemo(() => {
    /** @type {import("./SearchInfiniteProvider").SearchInfiniteContextValue<T>} */
    const value = {
      swrInfiniteResponse,
      reset,
      appliedFilters,
      submit,
      form,
      autoSubmit,
      submittedFields,
      isDirty:
        JSON.stringify(defaultValues) !== JSON.stringify(submittedFields),
    };
    return value;
  }, [
    appliedFilters,
    autoSubmit,
    defaultValues,
    form,
    reset,
    submit,
    submittedFields,
    swrInfiniteResponse,
  ]);

  return searchInfiniteContextValue;
}
